import { Laptop, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DeviceChoice } from "@/lib/deviceSession";

/**
 * The one question we ever ask about staying signed in. Shown once per browser.
 */
export function DeviceTrustPrompt({
  onChoose,
  busy,
}: {
  onChoose: (choice: DeviceChoice) => void;
  busy?: boolean;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold">Is this your own device, or one shared with others?</h2>
        <p className="text-sm text-muted-foreground mt-1">
          We only ask this once on each device.
        </p>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => onChoose("personal")}
        className="w-full text-left rounded-lg border border-border p-4 min-h-[44px] hover:border-primary transition-colors disabled:opacity-60"
      >
        <span className="flex items-start gap-3">
          <Laptop className="h-5 w-5 mt-0.5 text-primary shrink-0" />
          <span>
            <span className="block font-medium">This is my device, keep me signed in</span>
            <span className="block text-sm text-muted-foreground">
              A phone, laptop or tablet only you use.
            </span>
          </span>
        </span>
      </button>

      <button
        type="button"
        disabled={busy}
        onClick={() => onChoose("shared")}
        className="w-full text-left rounded-lg border border-border p-4 min-h-[44px] hover:border-primary transition-colors disabled:opacity-60"
      >
        <span className="flex items-start gap-3">
          <Users className="h-5 w-5 mt-0.5 text-primary shrink-0" />
          <span>
            <span className="block font-medium">This is shared, sign me out automatically</span>
            <span className="block text-sm text-muted-foreground">
              A staff room computer, or a tablet more than one person logs into.
            </span>
          </span>
        </span>
      </button>

      <p className="text-xs text-muted-foreground">
        Either way, we sign you out if the device is left completely untouched for 8 hours.
      </p>

      {busy && (
        <Button variant="ghost" size="sm" disabled className="w-full">
          Signing you in…
        </Button>
      )}
    </div>
  );
}
